/**
 * API client composable using Axios.
 *
 * Provides configured Axios instance with:
 * - Base URL (proxied through Vite in dev)
 * - JWT token interceptor
 * - Error handling with redirect to login on 401
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios'
import type { ApiError } from '@/types/api'

/**
 * Get JWT token from localStorage.
 */
function getToken(): string | null {
  return localStorage.getItem('auth_token')
}

/**
 * Create configured Axios instance.
 */
function createApiClient(): AxiosInstance {
  const api = axios.create({
    baseURL: '/api/v1',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - add JWT token
  api.interceptors.request.use(
    (config) => {
      const token = getToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - handle errors
  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ApiError>) => {
      // Handle 401 Unauthorized - redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_profile')

        // Redirect to login (using window.location to avoid router context issues)
        if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        console.error('Access forbidden:', error.response.data)
      }

      // Handle 500 Internal Server Error
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data)
      }

      return Promise.reject(error)
    }
  )

  return api
}

// Singleton instance
const api = createApiClient()

/**
 * useApi composable.
 *
 * Provides typed Axios instance with authentication and error handling.
 *
 * @example
 * ```typescript
 * import { useApi } from '@/composables/useApi'
 *
 * const { api } = useApi()
 * const response = await api.get('/candidates')
 * ```
 */
export function useApi() {
  return {
    api,
  }
}

/**
 * Extract error message from API error.
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined

    if (apiError) {
      if (typeof apiError.detail === 'string') {
        return apiError.detail
      }

      if (Array.isArray(apiError.detail)) {
        // Validation errors
        return apiError.detail
          .map((err) => `${err.loc.join('.')}: ${err.msg}`)
          .join(', ')
      }
    }

    return error.message || 'An error occurred'
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unknown error occurred'
}

export default api
